<template>
  <Teleport to="body">
    <div v-show="visible && hasData" class="global-popup-overlay" @click="handleOverlayClick">
      <div class="global-popup-container" @click.stop>
        <!-- 图片 -->
        <img :src="getImageUrl(currentPopupData?.picture)" :alt="currentPopupData?.title || imageAlt"
          class="popup-image" @click="handleImageClick" />

        <!-- 所有弹窗都显示复选框 -->
        <div class="textbox">
          <img v-if="noReminderToday" :src="getAssetsFile('home/gouzhong.png')" alt="" class="check-icon"
            @click="noLonger">
          <div v-else class="checkbox-icon" @click="noLonger"></div>
          <div class="text-white text-[10px] ml-[5px]">{{ t('globalPopup.noRemindersToday') }}</div>
        </div>

        <!-- 关闭按钮 -->
        <img :src="getAssetsFile('home/zu16.png')" alt="" class="mt-[20px] w-[30px] h-[30px]" @click="handleClose">
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { getAssetsFile } from '@/utils/pub-use'
import * as GameApi from '@/api/game'
import { useUserStore } from '@/stores/modules/user'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const userStore = useUserStore()
const router = useRouter()
const isLoggedIn = computed(() => userStore.isLoggedIn)

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  imageSrc: {
    type: String,
    default: ''
  },
  imageAlt: {
    type: String,
    default: '弹窗图片'
  },
  checkboxText: {
    type: String,
    default: '今日不再弹出'
  },
  closeOnOverlay: {
    type: Boolean,
    default: true
  },
  preventScroll: {
    type: Boolean,
    default: true
  },
  popupData: {
    type: Object,
    default: null
  },
  popupList: {
    type: Array,
    default: () => []
  },
  autoFetch: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'update:visible', 'image-click', 'all-closed'])

const noReminderToday = ref(false)
const currentPopupIndex = ref(0)
const popupQueue = ref([])
const hasData = ref(false) // 是否有数据可以显示

// 计算当前显示的弹窗数据
const currentPopupData = computed(() => {
  if (props.popupData) {
    return props.popupData
  }
  if (popupQueue.value.length > 0 && currentPopupIndex.value < popupQueue.value.length) {
    return popupQueue.value[currentPopupIndex.value]
  }
  return null
})

// 获取环境变量中的图片URL前缀
const VITE_IMG_URL = import.meta.env.VITE_IMG_URL

// 解析多语言图片数据
const parsePictureData = (pictureStr) => {
  if (!pictureStr) return ''

  try {
    // 首先尝试解析为 JSON
    if (typeof pictureStr === 'string' && pictureStr.startsWith('{')) {
      const pictureData = JSON.parse(pictureStr)
      // 根据当前语言返回对应图片
      const currentLang = localStorage.getItem('language') || 'en'

      // 处理语言代码映射：en-US -> en, tl-PH -> tl
      let mappedLang = currentLang
      if (currentLang === 'en-US') {
        mappedLang = 'en'
      }
      if (currentLang === 'tl-PH') {
        mappedLang = 'tl'
      }
      if (currentLang === 'pt-BR') {
        mappedLang = 'pt'
      }
      if (currentLang === 'hi-IN') {
        mappedLang = 'hi'
      }

      // 添加调试信息
      console.log('GlobalPopup Language Debug:', {
        currentLang,
        mappedLang,
        availableLanguages: Object.keys(pictureData),
        pictureData
      })

      const result = pictureData[currentLang]?.img || pictureData[mappedLang]?.img || pictureData['en']?.img || pictureData['tl']?.img || pictureStr
      console.log('GlobalPopup Selected Image:', result)

      return result
    }

    // 如果不是 JSON 格式，直接返回原始字符串
    return pictureStr
  } catch (error) {
    // 解析失败时，直接返回原始字符串
    return pictureStr
  }
}

// 获取图片URL
const getImageUrl = (picturePath) => {
  if (!picturePath) return props.imageSrc

  // 解析多语言图片数据
  const parsedPicture = parsePictureData(picturePath)

  // 如果已经是完整的URL，直接返回
  if (parsedPicture.startsWith('http://') || parsedPicture.startsWith('https://')) {
    return parsedPicture
  }

  // 使用环境变量拼接图片前缀
  if (VITE_IMG_URL) {
    return VITE_IMG_URL + parsedPicture
  }

  // 如果没有环境变量，返回原始路径
  return parsedPicture
}

// 修改获取稳定用户标识的函数
const getStableUserId = () => {
  // 如果用户已登录且有用户信息，使用用户ID并保存
  if (isLoggedIn.value && userStore.userInfo?.id) {
    const currentId = userStore.userInfo.id.toString()
    const savedId = localStorage.getItem('stable_user_id')

    // 如果保存的ID与当前ID不同，说明是不同用户登录，清除旧的弹窗状态
    if (savedId && savedId !== currentId) {
      clearPopupState(savedId)
    }

    // 保存新的稳定ID
    localStorage.setItem('stable_user_id', currentId)
    return currentId
  }

  // 如果用户未登录，尝试从本地存储获取之前保存的ID
  const savedId = localStorage.getItem('stable_user_id')
  if (savedId && savedId !== 'anonymous') {
    return savedId
  }

  return 'anonymous'
}

// 添加清除弹窗状态的函数
const clearPopupState = (oldUserId) => {
  const keys = Object.keys(localStorage)
  keys.forEach(key => {
    if (key.includes(`popup_`) && key.includes(`_${oldUserId}_`)) {
      localStorage.removeItem(key)
    }
  })
  // 也清除 stable_user_id
  localStorage.removeItem('stable_user_id')
}

// 修改 handleUserLogin 函数，确保标记清除后立即触发弹窗检查
const handleUserLogin = () => {
  setTimeout(() => {
    if (isLoggedIn.value && userStore.userInfo?.id) {
      const currentId = userStore.userInfo.id.toString()
      const savedId = localStorage.getItem('stable_user_id')

      if (savedId && savedId !== currentId) {
        clearPopupState(savedId)
      }

      // 保存新的稳定ID
      localStorage.setItem('stable_user_id', currentId)

      // 清除所有 type=1 弹窗的登录访问标记，让它们重新显示
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.includes('popup_login_visit_')) {
          localStorage.removeItem(key)
        }
      })

      console.log('GlobalPopup: User login state updated, login visit marks cleared')

      // 不在这里自动检查弹窗，由优先级逻辑统一控制
      // 优先级逻辑会在登录后统一检查并显示弹窗
    } else {
      setTimeout(handleUserLogin, 1000)
    }
  }, 100)
}

// 添加页面刷新检测
const isPageRefresh = ref(false)

// 检测页面是否是通过刷新加载的
const detectPageRefresh = () => {
  // 使用 localStorage 检测页面刷新
  const pageRefreshKey = 'page_refresh_detected'
  const wasRefreshed = localStorage.getItem(pageRefreshKey)

  if (wasRefreshed === 'true') {
    // 如果有标记，说明是页面刷新
    isPageRefresh.value = true
    localStorage.removeItem(pageRefreshKey) // 清除标记，避免下次误判
  } else {
    // 如果没有标记，说明是页面切换
    isPageRefresh.value = false
  }
}

// 在页面即将卸载时设置标记
const handleBeforeUnload = () => {
  localStorage.setItem('page_refresh_detected', 'true')
}

// 修改 shouldShowPopup 函数，添加调试信息
const shouldShowPopup = (popup) => {
  const today = new Date().toDateString()

  // 使用稳定的用户标识
  const userId = getStableUserId()

  // 检查用户是否选择了今日不再弹出
  const noReminderKey = `popup_no_reminder_${popup.id}_${userId}_${today}`
  const noReminderToday = localStorage.getItem(noReminderKey)
  if (noReminderToday === 'true') {
    console.log(`GlobalPopup: Popup ${popup.id} skipped - user chose no reminder today`)
    return false
  }

  // 根据 popupGroup 判断用户状态是否匹配
  if (popup.popupGroup === 2 && !isLoggedIn.value) {
    console.log(`GlobalPopup: Popup ${popup.id} skipped - login required but user not logged in`)
    return false // 登录用户弹窗，但用户未登录
  }
  if (popup.popupGroup === 1 && isLoggedIn.value) {
    console.log(`GlobalPopup: Popup ${popup.id} skipped - for non-logged users but user is logged in`)
    return false // 未登录用户弹窗，但用户已登录
  }

  // 根据 type 判断弹窗时机
  switch (popup.type) {
    case 1: // 每次登录或访问
      if (isLoggedIn.value) {
        // 已登录用户：检查是否已经为这次登录显示过
        const loginVisitKey = `popup_login_visit_${popup.id}_${userId}`
        const hasLoginVisited = localStorage.getItem(loginVisitKey)

        if (!hasLoginVisited) {
          // 这次登录还没有显示过，应该显示
          console.log(`GlobalPopup: Popup ${popup.id} will show - first login visit`)
          return true
        }

        console.log(`GlobalPopup: Popup ${popup.id} skipped - already shown for this login`)
        return false
      } else {
        // 未登录用户：只在页面刷新时弹出
        if (isPageRefresh.value) {
          console.log(`GlobalPopup: Popup ${popup.id} will show - page refresh for non-logged user`)
          return true
        } else {
          console.log(`GlobalPopup: Popup ${popup.id} skipped - no page refresh for non-logged user`)
          return false
        }
      }

    case 2: // 每日一次
      const dailyKey = `popup_daily_${popup.id}_${userId}_${today}`
      const dailyShown = localStorage.getItem(dailyKey)

      // 检查是否已经显示过
      const shouldShow = dailyShown !== 'true'

      if (shouldShow) {
        console.log(`GlobalPopup: Popup ${popup.id} will show - daily popup not shown today`)
      } else {
        console.log(`GlobalPopup: Popup ${popup.id} skipped - daily popup already shown today`)
      }

      return shouldShow

    case 3: // 只弹一次
      const onceKey = `popup_once_${popup.id}_${userId}`
      const onceShown = localStorage.getItem(onceKey)
      const shouldShowOnce = onceShown !== 'true'

      if (shouldShowOnce) {
        console.log(`GlobalPopup: Popup ${popup.id} will show - one-time popup not shown before`)
      } else {
        console.log(`GlobalPopup: Popup ${popup.id} skipped - one-time popup already shown`)
      }

      return shouldShowOnce

    case 4: // 每回大厅必弹 - 每次回到首页都弹出
      // 不需要检查是否已经显示过，每次都显示
      // 但需要确保用户状态匹配
      console.log(`GlobalPopup: Popup ${popup.id} will show - lobby popup (every time back to home)`)
      return true // 每次都显示（大厅页面触发）

    default:
      console.log(`GlobalPopup: Popup ${popup.id} skipped - unknown type: ${popup.type}`)
      return false
  }
}

// 添加一个标志位，防止退出登录时触发弹窗检查
const isLoggingOut = ref(false)

// 自动获取弹窗数据
const fetchAndShowPopups = async () => {
  // 如果正在退出登录，不执行弹窗检查
  if (isLoggingOut.value || sessionStorage.getItem('is_logging_out') === 'true') {
    return
  }

  try {
    // 直接调用 API 获取弹窗列表
    const response = await GameApi.batchGetGamesPopup()

    // 再次检查是否还在退出登录状态
    if (sessionStorage.getItem('is_logging_out') === 'true') {
      return
    }

    if (response && response.data) {
      // 只过滤 switchStatus 和 shouldShowPopup
      const allPopups = response.data.filter(popup => {
        const switchStatusOk = popup.switchStatus
        const shouldShow = shouldShowPopup(popup)

        return switchStatusOk && shouldShow
      })

      if (allPopups.length > 0) {
        // 按照weight字段排序，weight值越大优先级越高
        const sortedPopups = allPopups.sort((a, b) => {
          const weightA = a.weight || 0
          const weightB = b.weight || 0
          return weightB - weightA // 降序排列，weight大的在前
        })

        popupQueue.value = sortedPopups
        currentPopupIndex.value = 0
        // 不在这里自动显示，由优先级逻辑控制显示时机
      } else {
        // 清空弹窗队列
        popupQueue.value = []
        currentPopupIndex.value = 0
        // 如果没有符合条件的弹窗（包括勾选了"不再弹出"），关闭弹窗
        if (props.visible) {
          emit('update:visible', false)
        }
      }
    } else {
      // 清空弹窗队列
      popupQueue.value = []
      currentPopupIndex.value = 0
      // 如果没有数据，且弹窗是通过优先级控制显示的，不自动关闭
      // 这里不关闭，让优先级逻辑控制
    }
  } catch (error) {
    // 出错时也清空队列
    popupQueue.value = []
    currentPopupIndex.value = 0
    // 出错时不自动关闭弹窗，让优先级逻辑控制
  }
}

// 保留这个优化后的 silentCheckPopups 函数
const silentCheckPopups = async () => {
  // 如果正在退出登录，不执行弹窗检查
  if (isLoggingOut.value || sessionStorage.getItem('is_logging_out') === 'true') {
    return
  }

  try {
    // 直接调用 API 获取弹窗列表
    const response = await GameApi.batchGetGamesPopup()

    // 再次检查是否还在退出登录状态
    if (sessionStorage.getItem('is_logging_out') === 'true') {
      return
    }

    if (response && response.data) {
      // 只过滤 switchStatus 和 shouldShowPopup
      const allPopups = response.data.filter(popup => {
        const switchStatusOk = popup.switchStatus
        const shouldShow = shouldShowPopup(popup)

        return switchStatusOk && shouldShow
      })

      if (allPopups.length > 0) {
        // 按照weight字段排序，weight值越大优先级越高
        const sortedPopups = allPopups.sort((a, b) => {
          const weightA = a.weight || 0
          const weightB = b.weight || 0
          return weightB - weightA // 降序排列，weight大的在前
        })

        // 设置弹窗队列
        popupQueue.value = sortedPopups
        currentPopupIndex.value = 0
        // 不在这里自动显示，由优先级逻辑控制显示时机
      } else {
        // 清空弹窗队列
        popupQueue.value = []
        currentPopupIndex.value = 0
        // 只有在弹窗当前显示时才关闭
        if (props.visible) {
          emit('update:visible', false)
        }
      }
    } else {
      // 清空弹窗队列
      popupQueue.value = []
      currentPopupIndex.value = 0
      // 只有在弹窗当前显示时才关闭
      if (props.visible) {
        emit('update:visible', false)
      }
    }
  } catch (error) {
    // 出错时也清空队列
    popupQueue.value = []
    currentPopupIndex.value = 0
    // 只有在弹窗当前显示时才关闭
    if (props.visible) {
      emit('update:visible', false)
    }
  }
}

// 初始化弹窗队列
const initPopupQueue = () => {
  popupQueue.value = []
  currentPopupIndex.value = 0

  // 如果有 popupList，使用 popupList
  if (props.popupList && props.popupList.length > 0) {
    const filteredPopups = props.popupList.filter(popup =>
      popup.switchStatus && popup.noPopup !== 1
    )

    // 按照weight字段排序
    const sortedPopups = filteredPopups.sort((a, b) => {
      const weightA = a.weight || 0
      const weightB = b.weight || 0
      return weightB - weightA // 降序排列，weight大的在前
    })

    popupQueue.value = sortedPopups
  }
  // 如果有单个 popupData，添加到队列
  else if (props.popupData) {
    popupQueue.value = [props.popupData]
  }
  // 不在这里自动获取数据，由 watch(visible) 中的逻辑处理
}

// 显示下一个弹窗
const showNextPopup = () => {
  currentPopupIndex.value++

  if (currentPopupIndex.value < popupQueue.value.length) {
    // 还有下一个弹窗，继续显示
    return true
  } else {
    // 所有弹窗都显示完毕
    emit('all-closed', noReminderToday.value)
    return false
  }
}

// 修改 savePopupShown 函数
const savePopupShown = (popup) => {
  const today = new Date().toDateString()
  const userId = getStableUserId()

  // 如果用户勾选了今日不再弹出，保存到本地存储
  if (noReminderToday.value) {
    const noReminderKey = `popup_no_reminder_${popup.id}_${userId}_${today}`
    localStorage.setItem(noReminderKey, 'true')
  }

  // 根据 type 保存显示状态
  switch (popup.type) {
    case 1: // 每次登录或访问 - 在弹窗显示后设置标记
      const loginVisitKey = `popup_login_visit_${popup.id}_${userId}`
      localStorage.setItem(loginVisitKey, 'true')
      break

    case 2: // 每日一次
      const dailyKey = `popup_daily_${popup.id}_${userId}_${today}`
      localStorage.setItem(dailyKey, 'true')
      break

    case 3: // 只弹一次
      const onceKey = `popup_once_${popup.id}_${userId}`
      localStorage.setItem(onceKey, 'true')
      break

    case 4: // 每回大厅必弹 - 不需要保存状态，每次都显示
      // 不保存任何状态，确保下次回到首页时还能显示
      break
  }

  // 标记活动为已显示
  if (popup.activityId) {
    const activityKey = `popup_activity_${popup.activityId}_${userId}_${today}`
    localStorage.setItem(activityKey, 'true')
  }
}

// 处理关闭
const handleClose = () => {
  emit('close', noReminderToday.value, currentPopupData.value)

  // 保存当前弹窗的显示状态
  if (currentPopupData.value) {
    savePopupShown(currentPopupData.value)
  }

  // 检查是否还有下一个弹窗
  if (popupQueue.value.length > 1 && currentPopupIndex.value < popupQueue.value.length - 1) {
    // 显示下一个弹窗
    if (showNextPopup()) {
      return // 不关闭弹窗，继续显示下一个
    }
  }

  // 没有更多弹窗或只有一个弹窗，关闭弹窗
  emit('update:visible', false)
}

// 处理遮罩层点击
const handleOverlayClick = () => {
  if (props.closeOnOverlay) {
    handleClose()
  }
}

// 处理图片点击
const handleImageClick = () => {
  const currentData = currentPopupData.value

  if (currentData?.url) {
    // 检查是否是登录注册相关的URL参数
    if (currentData.url.includes('?reg=true') || currentData.url.includes('?login=true')) {
      // 解析URL参数
      const url = new URL(currentData.url, window.location.origin)
      const regParam = url.searchParams.get('reg')
      const loginParam = url.searchParams.get('login')
      const pidParam = url.searchParams.get('pid')

      // 先关闭当前弹窗
      emit('update:visible', false)

      // 延迟一下再处理登录注册弹窗，确保弹窗关闭完成
      setTimeout(() => {
        if (regParam === 'true') {
          // 显示注册弹窗
          const inviteCode = pidParam || sessionStorage.getItem('inviteCode') || ''
          window.$showRegisterDialog(inviteCode)
        } else if (loginParam === 'true') {
          // 显示登录弹窗
          const inviteCode = pidParam || sessionStorage.getItem('inviteCode') || ''
          window.$showLoginDialog(inviteCode)
        }
      }, 100)

      emit('image-click', currentData)
      return
    }

    // 根据 jumpType 字段判断跳转方式
    switch (currentData.jumpType) {
      case 1: // 内链
        try {
          router.push(currentData.url)
        } catch (error) {
          window.location.href = currentData.url
        }
        break

      case 2: // 外链
        // 检查是否是完整的URL
        if (currentData.url.startsWith('http://') || currentData.url.startsWith('https://')) {
          window.open(currentData.url, '_blank')
        } else {
          // 如果不是完整URL，添加协议前缀
          const fullUrl = currentData.url.startsWith('//')
            ? 'https:' + currentData.url
            : 'https://' + currentData.url
          window.open(fullUrl, '_blank')
        }
        break

      case 3: // 无跳转
        break

      default:
        if (currentData.url.startsWith('http://') || currentData.url.startsWith('https://')) {
          // 外链接，使用 window.open 在新窗口打开
          window.open(currentData.url, '_blank')
        } else {
          // 内链，使用 router.push 在当前窗口跳转
          try {
            router.push(currentData.url)
          } catch (error) {
            // 如果路由跳转失败，尝试使用 window.location
            window.location.href = currentData.url
          }
        }
        break
    }
  }
  emit('image-click', currentData)
}


// 防止背景滚动
const preventBodyScroll = () => {
  if (props.preventScroll) {
    document.body.style.overflow = 'hidden'
  }
}

const restoreBodyScroll = () => {
  if (props.preventScroll) {
    document.body.style.overflow = ''
  }
}

// 监听当前弹窗数据变化，更新复选框状态
watch(() => currentPopupData.value, () => {
  if (currentPopupData.value) {
    checkNoReminderToday()
  }
}, { immediate: true })

// 监听弹窗显示状态
watch(() => props.visible, async (newVal, oldVal) => {
  // 只有在 visible 从 false 变为 true 时才处理，避免 immediate 导致的自动触发
  if (newVal && !oldVal) {
    // 如果启用了自动获取，先获取数据检查是否有弹窗
    if (props.autoFetch) {
      // 先获取数据，检查是否有符合条件的弹窗
      await fetchAndShowPopups()
      // 如果没有数据，立即关闭，不显示弹窗框架
      if (popupQueue.value.length === 0) {
        hasData.value = false
        emit('update:visible', false)
        return
      }
      hasData.value = true
    } else {
      // 如果没有启用自动获取，检查是否有数据
      hasData.value = popupQueue.value.length > 0 || props.popupData !== null || (props.popupList && props.popupList.length > 0)
      if (!hasData.value) {
        emit('update:visible', false)
        return
      }
    }

    preventBodyScroll()
    // 如果启用了自动获取，数据已经在上面获取了，不需要再初始化队列
    // 如果没有启用自动获取，才初始化队列
    if (!props.autoFetch) {
      initPopupQueue()
    }
    // 检查当前弹窗的"今日不再弹出"状态
    if (currentPopupData.value) {
      checkNoReminderToday()
    }
  } else if (!newVal && oldVal) {
    // visible 从 true 变为 false
    hasData.value = false
    restoreBodyScroll()
  }
})

// 修改 noLonger 函数
const noLonger = () => {
  noReminderToday.value = !noReminderToday.value
  if (noReminderToday.value) {
    // 保存到特定弹窗的key
    const today = new Date().toDateString()
    const userId = getStableUserId()
    if (currentPopupData.value) {
      const noReminderKey = `popup_no_reminder_${currentPopupData.value.id}_${userId}_${today}`
      localStorage.setItem(noReminderKey, 'true')
    }
  } else {
    // 取消"今日不再弹出"
    const today = new Date().toDateString()
    const userId = getStableUserId()
    if (currentPopupData.value) {
      const noReminderKey = `popup_no_reminder_${currentPopupData.value.id}_${userId}_${today}`
      localStorage.removeItem(noReminderKey)
    }
  }
}

// 修改 checkNoReminderToday 函数
const checkNoReminderToday = () => {
  const today = new Date().toDateString()
  const userId = getStableUserId()

  // 检查当前弹窗是否被设置为今日不再弹出
  if (currentPopupData.value) {
    const noReminderKey = `popup_no_reminder_${currentPopupData.value.id}_${userId}_${today}`
    const noReminderToday = localStorage.getItem(noReminderKey)
    if (noReminderToday === 'true') {
      noReminderToday.value = true
      return
    }
  }

  noReminderToday.value = false
}

// 修改组件挂载逻辑
onMounted(() => {
  // 检测页面刷新
  detectPageRefresh()

  // 添加页面卸载监听
  window.addEventListener('beforeunload', handleBeforeUnload)

  if (props.visible && props.preventScroll) {
    preventBodyScroll()
  }

  // 不在这里自动检查弹窗，由优先级逻辑统一控制
  // 优先级逻辑会在页面加载时统一检查并显示弹窗
})

onUnmounted(() => {
  restoreBodyScroll()
  // 移除事件监听
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// 修改 watch(isLoggedIn) 中的登录逻辑
watch(() => isLoggedIn.value, (newLoginStatus, oldLoginStatus) => {
  // 如果正在退出登录，立即关闭弹窗并阻止所有检查
  if (sessionStorage.getItem('is_logging_out') === 'true' ||
    sessionStorage.getItem('force_close_popup') === 'true') {
    if (props.visible) {
      emit('update:visible', false)
    }
    return
  }

  // 如果用户刚登录
  if (newLoginStatus && !oldLoginStatus) {
    console.log('GlobalPopup: User logged in, checking popups...')

    // 先处理用户登录逻辑
    handleUserLogin()
    isLoggingOut.value = false // 重置标志位

    // 清除登录状态变化标记，确保弹窗能正常显示
    sessionStorage.removeItem('login_status_changed')

    // 不在这里自动检查弹窗，由优先级逻辑统一控制
    // 优先级逻辑会在登录后统一检查并显示弹窗
  }

  // 如果用户退出登录，立即关闭弹窗并设置标志位
  if (!newLoginStatus && oldLoginStatus) {
    console.log('GlobalPopup: User logged out, clearing state...')
    isLoggingOut.value = true

    // 立即关闭弹窗，避免闪烁
    if (props.visible) {
      emit('update:visible', false)
    }

    // 清空弹窗队列
    popupQueue.value = []
    currentPopupIndex.value = 0

    // 清除登录状态变化标记
    sessionStorage.removeItem('login_status_changed')

    // 延迟重置标志位，并检查是否需要显示未登录用户的弹窗
    setTimeout(() => {
      // 检查是否还在退出登录状态
      if (sessionStorage.getItem('is_logging_out') !== 'true') {
        isLoggingOut.value = false
        // 不在这里自动检查弹窗，由优先级逻辑统一控制
      }
    }, 0) // 从 1000ms 增加到 2000ms
  }
})
</script>

<style scoped>
.global-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  transition: none;
}

.global-popup-container {
  width: 80vw;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  transition: none;
  border-radius: 10px;
}

.popup-image {
  width: 100%;
  height: 350px;
  display: block;
  cursor: pointer;
  transition: none;
  border-radius: 10px;
}

.textbox {
  background: #004D37;
  border-top: 1px solid #029871;
  width: 150px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  margin-top: 10px;
  padding: 10px;
  align-items: center;
}

.check-icon {
  width: 15px;
  height: 15px;
  object-fit: contain;
}

.checkbox-icon {
  width: 15px;
  height: 15px;
  border-radius: 3px;
  background: #FFF;
  cursor: pointer;
}


/* 响应式设计 */
@media (max-width: 768px) {
  .global-popup-overlay {
    padding: 10px;
  }

  .global-popup-container {
    width: 90vw;
    max-width: 300px;
  }
}
</style>
